import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useCustomerService } from 'services/customers';
import { Customer } from 'types/customer';
import Button from 'components/Button';
import styles from './Customers.module.scss';

interface DeleteCustomerModalProps {
  show: boolean;
  onHide: () => void;
  customer: Customer | null;
  onSuccess: (customer: Customer) => void;
  onError: (message: string) => void;
}

function DeleteCustomerModal({
  show,
  onHide,
  customer,
  onSuccess,
  onError,
}: DeleteCustomerModalProps) {
  const customerService = useCustomerService();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      setReason('');
    }
  }, [show]);

  if (!customer) {
    return null;
  }

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const voidedCustomer = await customerService.voidCustomer(
        customer.id,
        reason.trim() || undefined,
      );
      onSuccess(voidedCustomer);
      onHide();
    } catch (error) {
      onError(
        error instanceof Error ? error.message : 'Failed to void customer',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
      backdrop="static"
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Void Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.voidDetails}>
          <div className={styles.detailRow}>
            <strong>Name:</strong>
            <span>{customer.name}</span>
          </div>
          {customer.email && (
            <div className={styles.detailRow}>
              <strong>Email:</strong>
              <span>{customer.email}</span>
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="voidReason" className={styles.label}>
            Reason for Voiding (Optional)
          </label>
          <textarea
            id="voidReason"
            placeholder="Enter the reason for voiding this customer..."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className={styles.textarea}
            rows={4}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button
          type="button"
          variant="outline"
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => {
            void handleConfirm();
          }}
          disabled={isSubmitting}
          className={styles.deleteButton}
        >
          {isSubmitting ? 'Voiding...' : 'Void Customer'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DeleteCustomerModal;
